import { trackEvent } from "src/Amplitude";
import { Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useEffect, useReducer } from "react";
import ComponentLayoutWrapper from "src/components/core/ComponentLayoutWrapper";
import PageLayoutController from "src/components/core/PageLayoutController";
import Layout from "../components/core/Layout";
import Holdings from "../components/wallets/header/Holdings";
import Instructions from "../components/wallets/header/Instructions";
import WalletHeaderMobile, { WalletHeaderDesktop } from "../components/wallets/header/WalletHeader";
import HoldingsList from "../components/wallets/holdings/HoldingsListMobile";
import HoldingsTable from "../components/wallets/holdings/HoldingsTable";
import { HoldingRes, WalletBalanceRes } from "src/store/features/api/generated";
import { setWalletHideLowBalances } from "src/store/features/settings.slice";
import { RootState, useAppDispatch, useAppSelector } from "src/store/store";
import api from "src/store/features/api";
import GuestBanner from "src/components/dashboard/GuestBanner";
import usePermission from "src/utils/hooks/usePermission";
import WalletOptionsMobile, {
  WalletOptionsDesktop,
} from "src/components/wallets/header/WalletOptions";
import { useUserSlice } from "src/store/features/user.slice";
import { WalletsSkeleton } from "src/components/common/Skeletons";
import HideLowBalances from "src/components/wallets/HideLowBalances";

const DESKTOP_LAYOUT = [
  ["HEADING", "HEADING", "HEADING", "HEADING", "HEADING", "HEADING"],
  ["BALANCE", "BALANCE", "BALANCE", "OPTIONS", "OPTIONS", "OPTIONS"],
  [".", ".", ".", ".", "HIDE_LOW_BALANCE", "HIDE_LOW_BALANCE"],
  ["HOLDINGS", "HOLDINGS", "HOLDINGS", "HOLDINGS", "HOLDINGS", "HOLDINGS"],
  [".", ".", "FINE_PRINT", "FINE_PRINT", ".", "."],
];

const MOBILE_LAYOUT = [
  ["HEADING", "HEADING"],
  ["BALANCE", "BALANCE"],
  ["OPTIONS", "OPTIONS"],
  ["HOLDINGS", "HOLDINGS"],
  ["FINE_PRINT", "FINE_PRINT"],
];

export default function Wallets(): JSX.Element {
  const theme = useTheme();
  // redux slices
  const { isUnverified } = useUserSlice();
  const { selectedCurrency, walletHideLowBalances } = useAppSelector(
    (state: RootState) => state.settings,
  );
  const { userPermission } = usePermission();
  const isGuest = userPermission === "guest" || isUnverified;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [useDefaultTheme, toggle] = useReducer((theme: any): boolean => !theme, true);

  const { data: user } = api.useGetUserDatav2Query();

  const reduxDispatch = useAppDispatch();

  const { data: walletBalance, isLoading: isLoadingBalance } = api.useGetWalletPageBalanceQuery(
    {
      baseCurrency: selectedCurrency,
    },
    { pollingInterval: 60000 }, // every 60 seconds
  );

  // amplitude tracking
  useEffect((): void => {
    trackEvent("Visit_Page", { page: "Wallet" });
  }, []);

  // =================== Hide Low Holdings Logic ===================
  const { data: walletHoldings, isLoading: isLoadingHoldings } = api.useGetWalletPageHoldingsQuery(
    { baseCurrency: selectedCurrency },
    { pollingInterval: 60000 },
  );
  const ignoreHoldings = React.useMemo(
    () => ["BTC", "ETH", user?.defaultCurrencyConfig.code],
    [user],
  );

  const [holdings, setHoldings] = React.useState<HoldingRes[]>([]);
  const [defaultCurrencyHolding, setDefaultCurrencyHolding] = React.useState<HoldingRes>();

  const filterHoldings = (): HoldingRes[] => {
    const filteredHoldings = walletHoldings?.filter((holding: HoldingRes): boolean => {
      const holdingCode = holding.currencyConfig.code;
      return ignoreHoldings.includes(holdingCode) || !holding.isLowBalance;
    });

    return filteredHoldings ? filteredHoldings : [];
  };

  // filter holdings and update holdings if user selects a diferent multi-account
  useEffect(() => {
    if (walletHoldings) {
      // set holdings when walletHoldings is fetched
      if (holdings.length <= 0) {
        setHoldings(walletHoldings);
      }
      // if low balance is toggled filter wallet holdings and set holdings
      if (walletHideLowBalances) {
        const filteredHoldings = filterHoldings();
        setHoldings(filteredHoldings);
      } else {
        setHoldings(walletHoldings);
      }

      const matchingHolding = walletHoldings.find(
        (holding) => holding.currencyConfig.code === user?.defaultCurrencyConfig.code,
      );

      setDefaultCurrencyHolding(matchingHolding ?? undefined);
    }
  }, [walletHoldings, walletHideLowBalances]);

  const hideLowHoldings = (
    event: React.ChangeEvent<HTMLInputElement> | null,
    checked: boolean,
  ): void => {
    const filteredHoldings = filterHoldings();
    if (checked) {
      reduxDispatch(setWalletHideLowBalances(true));
      setHoldings(filteredHoldings);
    } else {
      reduxDispatch(setWalletHideLowBalances(false));
      setHoldings(walletHoldings ?? []);
    }
  };

  const guestBalance = {
    balance: { value: 0, change: 0 },
    pending: 0,
  };

  return (
    <Layout toggleTheme={toggle} useDefaultTheme={useDefaultTheme}>
      <PageLayoutController
        mobileLayout={MOBILE_LAYOUT}
        desktopLayout={DESKTOP_LAYOUT}
        spacing={10}
        container={true}>
        {/* HEADING */}
        <ComponentLayoutWrapper
          gridArea="HEADING"
          state={{ isLoading: isGuest ? false : isLoadingBalance }}
          fadeInDirection="none"
          loadingComponent={<></>}>
          <Grid sx={{ display: "flex" }}>
            {isMobile ? <WalletHeaderMobile /> : <WalletHeaderDesktop />}
            {isMobile && !isGuest && (
              <WalletOptionsMobile defaultCurrencyHolding={defaultCurrencyHolding} />
            )}
          </Grid>
        </ComponentLayoutWrapper>

        {/* BALANCE */}
        <ComponentLayoutWrapper
          gridArea="BALANCE"
          fadeInDirection="none"
          state={{ isLoading: isGuest ? false : isLoadingBalance }}
          loadingComponent={<></>}>
          <Holdings walletBalance={isGuest ? (guestBalance as WalletBalanceRes) : walletBalance} />
        </ComponentLayoutWrapper>

        {/* OPTIONS */}
        <ComponentLayoutWrapper
          gridArea="OPTIONS"
          state={{ isLoading: isGuest ? false : isLoadingBalance }}
          fadeInDirection="none"
          loadingComponent={<></>}>
          {!isMobile && (
            <WalletOptionsDesktop
              hideLowHoldings={hideLowHoldings}
              defaultCurrencyHolding={defaultCurrencyHolding}
            />
          )}
          {isMobile && <HideLowBalances hideLowHoldings={hideLowHoldings} />}
        </ComponentLayoutWrapper>

        {isGuest && (
          <ComponentLayoutWrapper
            gridArea="HOLDINGS"
            state={{ isLoading: false }}
            fadeInDirection="none"
            loadingComponent={<WalletsSkeleton />}>
            <Grid item xs={12}>
              <GuestBanner variant={1} descriptionPrefix="To deposit funds" featureName="balance" />
            </Grid>
          </ComponentLayoutWrapper>
        )}

        {/* HOLDINGS */}
        {!isGuest && (
          <ComponentLayoutWrapper
            gridArea="HOLDINGS"
            state={{ isLoading: isLoadingHoldings }}
            fadeInDirection="none"
            loadingComponent={<WalletsSkeleton />}>
            {isMobile ? (
              <HoldingsList holdings={holdings} />
            ) : (
              <HoldingsTable holdings={holdings} />
            )}
          </ComponentLayoutWrapper>
        )}

        {/* FINE_PRINT */}
        <ComponentLayoutWrapper
          gridArea="FINE_PRINT"
          state={{}}
          fadeInDirection="none"
          loadingComponent={<></>}>
          <div style={{ height: "80px", display: "flex", alignItems: "center" }}>
            <Instructions options="secondary" />
          </div>
        </ComponentLayoutWrapper>
      </PageLayoutController>
    </Layout>
  );
}
